import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Event, Schedule } from "@mui/icons-material";
import { ClickAwayListener, TextField, Box, useTheme, useMediaQuery, InputAdornment, IconButton, } from "@mui/material";
import { LocalizationProvider, StaticDatePicker, } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import { createRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { NerdPickerWrapper } from "../nerdPickersWrapper";
import { CustomLuxonAdapter } from "./customLuxonAdapter";
const defaultlocaleStringFormat = {
    weekday: "long",
    month: "long",
    day: "2-digit",
};
/**
 *
 * @param {NerdDatePickerProps} props Props
 * @returns {ReactElement} NerdDateTimePicker
 */
export default function NerdDatePicker(props) {
    const { setOpenCalendar, openCalendar, textFieldProps, datePickerProps, onChange, value, hideIcon, localeStringFormat = defaultlocaleStringFormat, } = props;
    const [textFieldValue, setTextFieldValue] = useState(value !== null ? value.toLocaleString(localeStringFormat) : "");
    const [anchorEl, setAnchorEl] = useState(null);
    const [tmpValue, setTmpValue] = useState(value);
    const adapter = new CustomLuxonAdapter({ locale: i18n.language });
    const theme = useTheme();
    const isDownMd = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation("components", { i18n: i18n });
    const inputRef = createRef();
    const iconButtonRef = createRef();
    const [disabledDate, setDisabledDate] = useState(false);
    const handleClick = () => {
        if (!anchorEl) {
            setAnchorEl(iconButtonRef.current);
            setOpenCalendar?.(true);
        }
        else
            handleClose();
    };
    const handleClose = () => {
        if (anchorEl !== null) {
            setAnchorEl(null);
            setOpenCalendar?.(false);
            inputRef.current?.focus();
        }
    };
    useEffect(() => {
        setTextFieldValue(value?.toLocaleString(localeStringFormat) || "");
        setTmpValue(value);
    }, [value]);
    useEffect(() => {
        if (openCalendar === true)
            setAnchorEl(iconButtonRef.current);
        else
            handleClose();
    }, [openCalendar]);
    const onTextFieldValueChange = (newValue) => {
        setDisabledDate(false);
        setTextFieldValue(newValue);
    };
    const onTextFieldBlur = (e) => {
        if (e.relatedTarget?.classList.contains("MuiPickersDay-root"))
            inputRef.current?.focus();
        else {
            const date = adapter.date(e.target.value);
            if (date.isValid) {
                const newDate = value?.set({
                    day: date.day,
                    month: date.month,
                    ...(e.target.value.includes(date.year.toString()) && {
                        year: date.year,
                    }),
                });
                if (newDate?.toMillis() != value?.toMillis()) {
                    if (newDate && datePickerProps?.shouldDisableDate?.(newDate)) {
                        setDisabledDate(true);
                    }
                    else {
                        setTextFieldValue(date.toLocaleString(localeStringFormat));
                        onChange(newDate || null);
                    }
                }
                else
                    setTextFieldValue(value?.toLocaleString(localeStringFormat) || "");
            }
        }
    };
    const onDateChange = (newValue) => {
        const date = adapter.date(newValue);
        setTmpValue(date.isValid ? date : null);
        setTextFieldValue(date.toLocaleString(localeStringFormat));
        const newDate = value?.set({
            day: date.day,
            month: date.month,
            year: date.year,
        }) || date;
        if (newDate?.toMillis() !== value?.toMillis() && !isDownMd) {
            onChange(newDate || null);
        }
    };
    const getActions = () => {
        const minDate = datePickerProps?.minDate;
        const today = DateTime.now();
        if (minDate) {
            if (typeof minDate !== "string") {
                return minDate.startOf("day") > today.startOf("day") ? [] : ["today"];
            }
            else {
                return DateTime.fromISO(minDate).startOf("day") > today.startOf("day")
                    ? []
                    : ["today"];
            }
        }
        else
            return ["today"];
    };
    return (_jsx(ClickAwayListener, { onClickAway: handleClose, children: _jsx("div", { children: _jsxs(LocalizationProvider, { dateAdapter: CustomLuxonAdapter, adapterLocale: i18n.language, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center" }, children: [!hideIcon && (_jsx(Schedule, { sx: { color: "action.active", mr: 1, my: 0.5 } })), _jsx(TextField, { autoComplete: "off", inputRef: inputRef, focused: anchorEl === null ? false : true, fullWidth: true, label: t("nerdDatePicker.date"), size: "small", variant: "standard", ...textFieldProps, value: textFieldValue, InputProps: {
                                    ...textFieldProps?.InputProps,
                                    endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { disabled: textFieldProps?.disabled, ref: iconButtonRef, size: "small", onClick: (e) => {
                                                if (!textFieldProps?.disabled) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleClick();
                                                }
                                            }, onKeyDown: (e) => {
                                                if (e.code == "Enter" && !textFieldProps?.disabled) {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    handleClick();
                                                }
                                            }, children: _jsx(Event, {}) }) })),
                                }, onClick: (e) => {
                                    if (!textFieldProps?.disabled)
                                        handleClick();
                                    textFieldProps?.onClick?.(e);
                                }, onChange: (e) => {
                                    onTextFieldValueChange(e.target.value);
                                    textFieldProps?.onChange?.(e);
                                }, onBlur: (e) => {
                                    onTextFieldBlur(e);
                                    textFieldProps?.onBlur?.(e);
                                }, error: (textFieldValue && !adapter.date(textFieldValue).isValid) ||
                                    disabledDate ||
                                    textFieldProps?.error
                                    ? true
                                    : false, helperText: ((textFieldValue && !adapter.date(textFieldValue).isValid) ||
                                    disabledDate) &&
                                    !anchorEl
                                    ? t("nerdDatePicker.invalid_date")
                                    : textFieldProps?.helperText
                                        ? textFieldProps?.helperText
                                        : "" })] }), _jsx(NerdPickerWrapper, { popperProps: {
                            open: anchorEl === null ? false : true,
                            anchorEl: anchorEl,
                            sx: {
                                zIndex: theme.zIndex.modal,
                                boxShadow: theme.shadows[5],
                                borderRadius: "4px",
                                backgroundColor: "inherit",
                            },
                        }, dialogProps: {
                            open: anchorEl === null ? false : true,
                            onClose: handleClose,
                        }, dialogActionBarProps: {
                            onAccept() {
                                onChange(tmpValue);
                                handleClose();
                            },
                            onCancel() {
                                setTmpValue(value);
                                handleClose();
                            },
                            onClear() {
                                onChange(null);
                                handleClose();
                            },
                            onSetToday() {
                                onChange(DateTime.now());
                                handleClose();
                            },
                            actions: datePickerProps?.componentsProps?.actionBar?.actions || [
                                ...getActions(),
                                "cancel",
                                "accept",
                            ],
                        }, popperActionBarProps: {
                            onAccept() {
                                onChange(tmpValue);
                                handleClose();
                            },
                            onCancel() {
                                setTmpValue(value);
                                handleClose();
                            },
                            onClear() {
                                onChange(null);
                                handleClose();
                            },
                            onSetToday() {
                                onChange(DateTime.now());
                                handleClose();
                            },
                            actions: datePickerProps?.componentsProps?.actionBar?.actions ||
                                getActions(),
                        }, children: _jsx(Box, { sx: {
                                "& .MuiPickerStaticWrapper-content": {
                                    borderRadius: "4px",
                                },
                            }, children: _jsx(StaticDatePicker, { displayStaticWrapperAs: isDownMd ? "mobile" : "desktop", showToolbar: false, componentsProps: {
                                    actionBar: {
                                        actions: [],
                                    },
                                }, disableMaskedInput: true, ...datePickerProps, value: tmpValue, onChange: (value, keyboardInputValue) => {
                                    onDateChange(value);
                                    if (datePickerProps?.closeOnSelect && !isDownMd) {
                                        handleClose();
                                        inputRef.current?.focus();
                                    }
                                    datePickerProps?.onChange?.(value, keyboardInputValue);
                                }, renderInput: (params) => _jsx(TextField, { ...params }) }) }) })] }) }) }));
}
